import React, { useRef, useState } from 'react';

import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base';
import { ArtistListingItem } from '$partials/items';
import { Header } from '$partials/global';
import { Button } from '$partials/ui';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ArtistListingPage}
 */
const ArtistListingPage = (props) => {
    const [view, setView] = useState('list');
    const bodyRef = useRef();
    const page = props.data.page;
    const chunkArray = (arr, chunkCount) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += chunkCount){
            chunks.push(arr.slice(i, i + chunkCount));
        }
        return chunks;
    };

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data?.uri ?? '')}`,
        page: page.items?.page || 1,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
    });

    return (
        <>
            <Header
                {...props.data?.header}
                bodyRef={bodyRef}
                domain={props.domain}/>
            <main id='main-content'>
                <div className='px-layout-outer pt-[50px] sm:pt-[100px] sticky top-0 bg-white z-10'>
                    <div className='-mx-layout-col flex pb-32'>
                        <div className='px-layout-col w-full sm:w-2/7 gap-y-16 grid auto-rows-min'>
                            <h1>
                                {page.title}
                            </h1>
                            <div
                                className='rich-text max-w-[400px]'
                                dangerouslySetInnerHTML={{ __html: page.standfirst }} />
                        </div>
                    </div>
                    <div className='flex py-16 gap-x-12'>
                        <Button
                            className={`${view === 'list' ? 'underline underline-offset-2 ' : ''}type-style-s hover:text-text-default-secondary`}
                            onClick={()=>setView('list')}>
                            {'List view'}
                        </Button>
                        <Button
                            className={`${view === 'image' ? 'underline underline-offset-2 ' : ''}type-style-s hover:text-text-default-secondary`}
                            onClick={()=>setView('image')}>
                            {'Image view'}
                        </Button>
                    </div>
                </div>
                <AjaxListingContext.Provider value={listing}>
                    <div
                        className='bg-[white] z-10 relative flex flex-wrap'
                        ref={bodyRef}>
                        {view === 'image' && page.items?.edges?.length > 0 && chunkArray(listing.items, 5).map((items, batch) => {
                            const asset = items?.[4]?.node?.listingMedia?.video || items?.[4]?.node?.listingMedia?.image;
                            const isLandscape = asset && asset.width > asset.height;
                            return (
                                <div
                                    className='flex flex-wrap w-full'
                                    key={`artist-listings-batch-${batch}`}>
                                    <div className={`w-full ${batch % 2 === 0 ? 'sm:w-3/7' : 'sm:w-4/7'}`}>
                                        {items.map((itm, idx) => {
                                            if (idx === 4 && isLandscape) {
                                                return null;
                                            }
                                            if (idx % 2 === 0) {
                                                return (
                                                    <div key={itm.node.id}>
                                                        <ArtistListingItem {...itm.node}/>
                                                    </div>
                                                );
                                            }
                                        })}
                                    </div>
                                    <div className={`w-full ${batch % 2 !== 0 ? 'sm:w-3/7' : 'sm:w-4/7'}`}>
                                        {items.map((itm, idx) => {
                                            if (idx % 2 !== 0) {
                                                return (
                                                    <div key={itm.node.id}>
                                                        <ArtistListingItem {...itm.node}/>
                                                    </div>
                                                );
                                            }
                                        })}
                                    </div>
                                    {isLandscape && (
                                        <div className='w-full'>
                                            <ArtistListingItem {...items[4].node}/>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {view === 'list' && page.items?.edges?.length > 0 && (
                            <div className='pt-16 pb-48 px-layout-outer w-full'>
                                <div className='-mx-layout-col'>
                                    <div className=' flex flex-wrap w-full gap-y-24 '>
                                        {page.items.edges.map(item => {
                                            const itm = item.node;
                                            return (
                                                <div
                                                    className='w-full sm:w-3/7 flex items-center px-layout-col'
                                                    key={itm.id}>
                                                    <a
                                                        className='expand-interaction__action hover:text-text-default-secondary'
                                                        href={itm.url}>
                                                        {itm.title}{itm.role && `, ${itm.role}`}
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* No result */}
                        {listing.items?.length === 0 && (
                            <div className='px-layout-outer type-style-s'>
                                {'No results'}
                            </div>
                        )}
                        <InfinitePagination />
                    </div>
                </AjaxListingContext.Provider>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default ArtistListingPage;
